// Training Map — admin-only "site map" of every route in the app,
// organized by category, with descriptions + who-uses-it metadata. Each
// row links straight to the route in a new tab so admins can jump from
// the map into the live page. Curated list — kept in sync manually with
// /app/frontend/src/App.js when new routes are added.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink, Lock, Globe, Shield, Users, Tv, Radio, ShoppingBag, Settings, Newspaper, Heart, BookOpen, Mic, MessageCircle, Trophy, Sparkles } from "lucide-react";

// ---- Curated route inventory ----
// Each entry: { path, label, desc, audience, scope, icon, hideFromList? }
// audience: "public" | "logged-in" | "creator" | "admin" | "kid" | "host"
// scope: "core" | "kids" | "studio" | "social" | "shop" | "admin" | "auth" | "radio" | "news"
const SECTIONS = [
  {
    key: "core", title: "Core surface", icon: Tv, items: [
      { path: "/", label: "Home", desc: "Marketing splash + hero CTAs", audience: "public" },
      { path: "/dashboard", label: "Dashboard", desc: "Logged-in user/creator hub", audience: "logged-in" },
      { path: "/tv", label: "TV grid", desc: "On-demand show grid", audience: "public" },
      { path: "/tv-guide", label: "TV Guide", desc: "Schedule grid (24/7 channels)", audience: "public" },
      { path: "/originals", label: "Tap In Originals", desc: "Network-produced shows", audience: "public" },
      { path: "/live", label: "Live Now", desc: "All currently-live broadcasts", audience: "public" },
      { path: "/shorts", label: "Shorts feed", desc: "Vertical 9:16 reel of stories + clips", audience: "public" },
      { path: "/awards", label: "Awards", desc: "Tap In Awards landing", audience: "public" },
    ],
  },
  {
    key: "studio", title: "Creator studio (broadcasting)", icon: Mic, items: [
      { path: "/studio/live", label: "Director's Mode", desc: "Main go-live cockpit (camera + scenes + overlays)", audience: "creator" },
      { path: "/studio/assist", label: "Assistant Director — Cockpit", desc: "Remote helper screen (manage scenes & guests for the host)", audience: "creator" },
      { path: "/studio/assist/redeem", label: "Assistant Token redeem", desc: "Claim an assistant invite link", audience: "creator" },
      { path: "/studio", label: "Full Studio (legacy)", desc: "Older studio dashboard", audience: "creator" },
      { path: "/phone/camera/abc", label: "Phone Camera (paired)", desc: "Turn your phone into a webcam (pair via QR from studio)", audience: "creator" },
      { path: "/reels/create", label: "Reels create", desc: "Upload / record a vertical short", audience: "creator" },
      { path: "/apply-creator", label: "Apply to be a Creator", desc: "Onboarding form", audience: "logged-in" },
    ],
  },
  {
    key: "radio", title: "Radio (DJ network)", icon: Radio, items: [
      { path: "/radio", label: "Radio home", desc: "All stations + Live Hosts rail", audience: "public" },
      { path: "/radio/host", label: "DJ Studio", desc: "Live DJ console (decks + crossfader + FX)", audience: "host" },
      { path: "/radio/apply-host", label: "Apply to be a DJ Host", desc: "Host application form", audience: "logged-in" },
    ],
  },
  {
    key: "news", title: "Tap In News", icon: Newspaper, items: [
      { path: "/tap-in-news", label: "News Feed", desc: "TMZ-style feed: breaking + categories + lead story", audience: "public" },
      { path: "/tap-in-news/:slug", label: "News article", desc: "Single article reader with social share unfurls", audience: "public" },
    ],
  },
  {
    key: "social", title: "Social & community", icon: Users, items: [
      { path: "/social", label: "Social feed", desc: "Public feed", audience: "public" },
      { path: "/social/explore", label: "Explore", desc: "Discover creators + posts", audience: "public" },
      { path: "/social/me", label: "My profile", desc: "Your social profile", audience: "logged-in" },
      { path: "/social/messages", label: "Messages", desc: "DM inbox", audience: "logged-in" },
      { path: "/social/notifications", label: "Notifications", desc: "Alert center", audience: "logged-in" },
      { path: "/social/welcome", label: "Welcome (onboarding)", desc: "First-time creator welcome", audience: "logged-in" },
      { path: "/community", label: "Community boards", desc: "Discussion threads", audience: "public" },
      { path: "/timeline", label: "Timeline", desc: "Activity timeline", audience: "logged-in" },
    ],
  },
  {
    key: "kids", title: "Tap In Kids", icon: Heart, items: [
      { path: "/kids", label: "Kids hub", desc: "Curated, kid-safe surface", audience: "kid" },
      { path: "/kids/community", label: "Kids community", desc: "Moderated kid social", audience: "kid" },
      { path: "/kids/games", label: "Kids games", desc: "Mini-games hub", audience: "kid" },
      { path: "/kids/shop", label: "Kids shop", desc: "Kid-safe merch", audience: "kid" },
      { path: "/kids/radio", label: "Kids radio", desc: "Music for kids", audience: "kid" },
      { path: "/kids/tutoring", label: "Kids tutoring", desc: "Live 1:1 tutoring sessions", audience: "kid" },
      { path: "/kids/become-creator", label: "Become a Kid Creator", desc: "Apply with parent consent", audience: "kid" },
      { path: "/parent/dashboard", label: "Parent dashboard", desc: "Approve content + activity for child accounts", audience: "logged-in" },
    ],
  },
  {
    key: "shop", title: "Shop & monetization", icon: ShoppingBag, items: [
      { path: "/shop", label: "Shop", desc: "Merch + creator items", audience: "public" },
      { path: "/cart", label: "Cart", desc: "Checkout cart", audience: "logged-in" },
      { path: "/orders", label: "Orders", desc: "Past orders", audience: "logged-in" },
      { path: "/subscribe", label: "Subscribe / Premium", desc: "Subscription tiers", audience: "logged-in" },
      { path: "/wallet", label: "Wallet", desc: "Tipping wallet + payouts", audience: "logged-in" },
      { path: "/sponsors", label: "Sponsors landing", desc: "Sponsorship pitch", audience: "public" },
      { path: "/sponsorship", label: "Sponsorship details", desc: "Package details + apply", audience: "public" },
      { path: "/contest", label: "Contests", desc: "Active platform contests", audience: "public" },
    ],
  },
  {
    key: "auth", title: "Auth & settings", icon: Lock, items: [
      { path: "/login", label: "Login", desc: "Email/password + Google OAuth", audience: "public" },
      { path: "/signup", label: "Signup", desc: "Create account", audience: "public" },
      { path: "/register", label: "Register", desc: "Alt signup route", audience: "public" },
      { path: "/auth/callback", label: "Auth callback", desc: "OAuth redirect handler (do not link)", audience: "public" },
      { path: "/verify-email", label: "Verify email", desc: "Email verification landing", audience: "public" },
      { path: "/profile/edit", label: "Profile edit", desc: "Edit display name + avatar + bio", audience: "logged-in" },
      { path: "/settings/notifications", label: "Notification settings", desc: "Toggle alert types", audience: "logged-in" },
    ],
  },
  {
    key: "support", title: "Trust & support", icon: Shield, items: [
      { path: "/support", label: "Support / Help Center", desc: "FAQ + contact pathways", audience: "public" },
      { path: "/contact", label: "Contact", desc: "Contact form", audience: "public" },
      { path: "/conference", label: "Conference rooms", desc: "Multi-user video rooms", audience: "logged-in" },
      { path: "/talent-search", label: "Talent search", desc: "Find creators by skill", audience: "public" },
      { path: "/go-live", label: "Creator Blueprint", desc: "6-step go-live guide for creators", audience: "public" },
    ],
  },
  {
    key: "admin", title: "Admin only", icon: Settings, items: [
      { path: "/admin", label: "Admin Panel", desc: "Top-level admin dashboard with all tabs", audience: "admin" },
      { path: "/admin?tab=news", label: "Admin → Tap In News", desc: "Authoring TMZ-style stories", audience: "admin" },
      { path: "/admin?tab=training", label: "Admin → Training Map", desc: "This page — site map + creator blueprint shortcuts", audience: "admin" },
    ],
  },
];

const AUDIENCE_TINT = {
  public:    "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  "logged-in": "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  creator:   "bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30",
  host:      "bg-blue-500/15 text-blue-300 ring-blue-500/30",
  kid:       "bg-pink-500/15 text-pink-300 ring-pink-500/30",
  admin:     "bg-yellow-400/15 text-yellow-300 ring-yellow-400/30",
};

export default function TrainingMapTab() {
  const [q, setQ] = useState("");

  const totalRoutes = useMemo(() => SECTIONS.reduce((acc, s) => acc + s.items.length, 0), []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return SECTIONS;
    return SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((it) => `${it.label} ${it.path} ${it.desc} ${it.audience}`.toLowerCase().includes(needle)) }))
      .filter((s) => s.items.length > 0);
  }, [q]);

  return (
    <div data-testid="training-map-tab" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-display font-black tracking-tight">Training Map</h2>
          <p className="text-white/60 text-sm">Every route in the app, organized by section. Click any path to open it in a new tab.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-white/50">{totalRoutes} routes mapped</div>
          <Link to="/go-live" target="_blank" rel="noreferrer" data-testid="training-map-blueprint-link" className="bg-tv-accent hover:bg-yellow-300 text-black px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Open Creator Blueprint
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md" data-testid="training-map-search-wrap">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          data-testid="training-map-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter routes (e.g. studio, kids, news)…"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-black/40 ring-1 ring-white/15 text-sm text-white placeholder:text-white/40 focus:ring-tv-accent outline-none"
        />
      </div>

      {/* Audience legend */}
      <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wider font-bold" data-testid="training-map-legend">
        {Object.entries(AUDIENCE_TINT).map(([k, c]) => (
          <span key={k} className={`px-2 py-0.5 rounded ring-1 ${c}`}>{k}</span>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-5">
        {filtered.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} data-testid={`training-map-section-${s.key}`} className="rounded-xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
              <div className="px-4 py-3 bg-black/40 flex items-center gap-2 border-b border-white/5">
                <Icon className="w-4 h-4 text-tv-accent" />
                <h3 className="font-display font-bold tracking-tight">{s.title}</h3>
                <span className="text-xs text-white/40 ml-1">({s.items.length})</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {s.items.map((it) => (
                    <tr key={it.path} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-2.5">
                        <a href={it.path} target="_blank" rel="noreferrer" className="text-tv-accent hover:underline font-mono text-xs inline-flex items-center gap-1">
                          {it.path} <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                      </td>
                      <td className="px-4 py-2.5 font-bold">{it.label}</td>
                      <td className="px-4 py-2.5 text-white/65">{it.desc}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ring-1 ${AUDIENCE_TINT[it.audience] || "bg-white/10 text-white/60 ring-white/15"}`}>
                          {it.audience}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-white/40" data-testid="training-map-empty">
            No routes match <span className="text-white/70">"{q}"</span>.
          </div>
        )}
      </div>
    </div>
  );
}
